/**
 * WorkerDetail — Detail view for a single worker.
 * Compact 4-column layout, all known fields, plus highlighted sections
 * for Health Insurance, Work Permit, Salary.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box, Stack, Typography, Avatar, Chip, IconButton,
  Tooltip, Grid, Button, LinearProgress, Alert, Card, CardContent,
  Snackbar, CircularProgress, TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ApartmentIcon from '@mui/icons-material/Apartment';
import NotesIcon from '@mui/icons-material/Notes';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiService from '../../services/apiServices';
import { ageFromBirthDate, formatDMY } from '../../services/dateFormats';
import ModuleCustomFields from '../dynamic/ModuleCustomFields';
import useConfirm from '../../hooks/useConfirm';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import { isPendingActivation } from '../../utils/activationStatus';

// ── Compact field cell ───────────────────────────────────────────────────────
// Fusha bosh NUK vizatohet. Profili i një punëtori të sapokrijuar kishte mbi
// njëzet rreshta «Not provided» — dyfishi i atyre që kishin vërtet një vlerë —
// dhe syri e humbte të dhënën mes tyre. Emrat e fushave që mungojnë nuk humbin:
// `DetailSection` i mbledh dhe i shkruan në një rresht të vetëm poshtë.
// Një kartelë brenda kolonave CSS: nuk lejohet të ndahet mes dy kolonash dhe e
// mban vetë hapësirën poshtë (`columnGap` mbulon vetëm anët).
const CARD_COL = { breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', display: 'block', mb: 2.5 };

const isFieldEmpty = (v) => v === undefined || v === null || v === '' || v === '—';

function DetailField({ label, value, span = 1, suffix, showEmpty = false }) {
  if (isFieldEmpty(value) && !showEmpty) return null;
  const empty = isFieldEmpty(value);
  return (
    <Grid item xs={6} sm={4} md={span === 2 ? 6 : 3}>
      <Box>
        <Typography
          sx={{
            display: 'block',
            mb: 0.25,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.disabled',
            letterSpacing: '0.04em',
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.9375rem',
            color: empty ? 'text.disabled' : 'text.primary',
            fontWeight: empty ? 400 : 500,
            lineHeight: 1.4,
            fontStyle: empty ? 'italic' : 'normal',
          }}
        >
          {empty
            ? 'Pa të dhënë'
            : (<>{value}{suffix}</>)}
        </Typography>
      </Box>
    </Grid>
  );
}

// ── Section wrapper ──────────────────────────────────────────────────────────
// Clean white card · subtle shadow · soft icon container · no colored side border
function DetailSection({ title, icon, accent, headerExtra, children, dense }) {
  // `accent` is preserved in the API for backward compat but no longer drives a
  // colored left border — only tints the small icon container.
  //
  // Kartela ndan fushat me vlerë nga ato pa vlerë. Emrat e atyre pa vlerë nuk
  // fshihen — mblidhen në një rresht të vetëm poshtë, sepse për HR-in «çfarë
  // mungon» është punë për t'u bërë, jo zhurmë për t'u lexuar njëzet herë.
  const kids = React.Children.toArray(children);
  const isField = (el) => React.isValidElement(el) && el.props
    && Object.prototype.hasOwnProperty.call(el.props, 'label')
    && Object.prototype.hasOwnProperty.call(el.props, 'value');
  const isBlank = (el) => isField(el) && isFieldEmpty(el.props.value) && !el.props.showEmpty;
  const missing = kids.filter(isBlank).map((el) => el.props.label);
  const shown   = kids.filter((el) => !isBlank(el));
  const allBlank = shown.length === 0;

  return (
    <Card
      elevation={0}
      sx={{
        border: 1, borderColor: 'divider',
        borderRadius: 2,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 2, sm: 2.5 },
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: accent ? alpha(accent, 0.10) : 'primary.soft',
              color: accent || 'primary.main',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            color: 'text.primary',
            letterSpacing: '-0.005em',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {headerExtra && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {headerExtra}
          </Box>
        )}
      </Box>
      <CardContent
        sx={{
          flex: 1,
          pt: 0.5,
          pb: { xs: '16px !important', sm: '18px !important' },
          px: { xs: 2, sm: 2.5 },
        }}
      >
        {allBlank ? (
          <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled', fontStyle: 'italic' }}>
            Pa të dhëna ende{missing.length > 0 ? ` — ${missing.length} fusha plotësohen te «Ndrysho»` : ''}.
          </Typography>
        ) : (
          <Grid container spacing={dense ? 2 : 2.5} rowSpacing={2}>
            {shown}
          </Grid>
        )}

        {!allBlank && missing.length > 0 && (
          <Box sx={{ mt: 2, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', lineHeight: 1.6 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Pa plotësuar ({missing.length}):</Box>{' '}
              {missing.join(' · ')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Reusable IntegrationCard ────────────────────────────────────────────────
// Wraps Slack/Telegram bindings in a consistent premium card with a soft icon
// container, title, status chip, description, and a body slot for actions.
function IntegrationCard({ icon, brandColor, title, description, status, children }) {
  return (
    <Box sx={CARD_COL}>
      <Card
        elevation={0}
        sx={{
          border: 1, borderColor: 'divider', borderRadius: 2,
          height: '100%',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(brandColor, 0.10),
              color: brandColor,
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 18 } })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.005em' }}>
                {title}
              </Typography>
              {status}
            </Stack>
            {description && (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.5, lineHeight: 1.5 }}>
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        {children && (
          <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 }, pt: 0.5 }}>
            {children}
          </Box>
        )}
      </Card>
    </Box>
  );
}

// ── Slack binding — auto-matches worker by email; admin can bind manually ──
function SlackBinding({ workerId }) {
  const [state, setState] = useState({ loading: true });
  const [manualId, setManualId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await apiService().get(`notifications/slack/lookup/${workerId}`);
      if (r.success) setState({ loading: false, ...r.data });
      else setState({ loading: false, found: false, reason: r.error });
    } catch (e) {
      setState({ loading: false, found: false, reason: e.message });
    }
  };

  const bindManually = async () => {
    if (!/^U[A-Z0-9]+$/.test(manualId.trim())) {
      setMsg({ severity: 'error', text: 'ID-ja duhet të fillojë me U dhe pastaj shifra/shkronja (p.sh. U0123ABC).' });
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const r = await apiService().post(`notifications/slack/bind/${workerId}`, { slackUserId: manualId.trim() });
      if (r.success) {
        setMsg({ severity: 'success', text: 'Lidhja u ruajt.' });
        setManualId('');
        load();
      } else setMsg({ severity: 'error', text: r.error || 'Gabim gjatë ruajtjes' });
    } catch (e) { setMsg({ severity: 'error', text: e.message }); }
    setBusy(false);
  };

  const [confirm, confirmUI] = useConfirm();


  const unbind = async () => {
    if (!(await confirm({ title: 'Hiq lidhjen e Slack', message: 'Hiq lidhjen e Slack për këtë punëtor?', confirmLabel: 'Fshi' }))) return;
    setBusy(true); setMsg(null);
    try {
      const r = await apiService().delete(`notifications/slack/bind/${workerId}`);
      if (r.success) { setMsg({ severity: 'success', text: 'Lidhja u hoq.' }); load(); }
      else setMsg({ severity: 'error', text: r.error || 'Gabim' });
    } catch (e) { setMsg({ severity: 'error', text: e.message }); }
    setBusy(false);
  };

  useEffect(() => { if (workerId) load(); }, [workerId]);

  const SLACK_PURPLE = '#4A154B';
  const isConnected = state.found;
  const statusChip = state.loading
    ? null
    : isConnected
      ? <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={state.cached ? 'I lidhur (cache)' : 'I lidhur'} size="small" color="success" sx={{ height: 22, fontWeight: 600 }} />
      : <Chip label="Pa lidhje" size="small" sx={{ height: 22, fontWeight: 600, bgcolor: 'grey.100', color: 'text.secondary', border: 0 }} />;

  return (
    <IntegrationCard
      icon={<EmailIcon />}
      brandColor={SLACK_PURPLE}
      title="Slack"
      description="Njoftime automatike përmes DM kur punëtori është anëtar i workspace-it Slack me të njëjtin email."
      status={statusChip}
    >
      {state.loading ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={14} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Po kontrollohet lidhja…</Typography>
        </Stack>
      ) : isConnected ? (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {state.user?.avatar && (
              <Box component="img" src={state.user.avatar} alt="" sx={{ width: 28, height: 28, borderRadius: '50%' }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                {state.user?.displayName || state.user?.realName || state.user?.username || state.user?.userId}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                ID: <Box component="code" sx={{ fontFamily: 'monospace' }}>{state.user?.userId}</Box>
                {state.email && <> · {state.email}</>}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Rifresko">
                <IconButton size="small" onClick={load}>
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Hiq lidhjen">
                <IconButton size="small" color="error" onClick={unbind} disabled={busy}>
                  <LinkOffIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          {state.verifiedAt && (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
              Verifikuar: {new Date(state.verifiedAt).toLocaleString('sq-AL')}
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
            {state.reason || 'Nuk u gjet asnjë user Slack me email-in e këtij punëtori. Mund ta lidhësh manualisht me Slack Member ID (profili → ⋮ → Copy member ID).'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              placeholder="U0123ABCDEF"
              value={manualId}
              onChange={(e) => setManualId(e.target.value.toUpperCase())}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, letterSpacing: 0.5 } }}
              sx={{ minWidth: 180, flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={bindManually}
              disabled={busy || !manualId}
              sx={{ bgcolor: SLACK_PURPLE, '&:hover': { bgcolor: '#3a0f3c' }, height: 40 }}
            >
              Lidh
            </Button>
            <Button variant="text" onClick={load} sx={{ height: 40 }}>
              Rifresko
            </Button>
          </Stack>
          {msg && <Alert severity={msg.severity} sx={{ py: 0.25 }}>{msg.text}</Alert>}
        </Stack>
      )}
      {confirmUI}
    </IntegrationCard>
  );
}

// ── Telegram binding — generates the /start link and shows linked status ─────
function TelegramBinding({ workerId }) {
  const [regs, setRegs] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [link, setLink] = useState(null);
  const [botUsername, setBotUsername] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const loadRegs = async () => {
    try {
      const r = await apiService().get(`notifications/registrations/${workerId}`);
      if (r.success) setRegs(r.data.registrations || []);
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => { if (workerId) loadRegs(); }, [workerId]);

  const tg = (regs || []).find((x) => x.channel === 'telegram');
  const isLinked = !!(tg && tg.external_id && tg.verified_at);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await apiService().post('notifications/telegram/register-link', { workerId });
      if (r.success) {
        setLink(r.data.link);
        setBotUsername(r.data.botUsername);
        setSnack({ open: true, msg: 'Linku u gjenerua. Hape në Telegram brenda 24 orësh.', severity: 'success' });
      } else {
        setSnack({ open: true, msg: r.error || 'Gabim', severity: 'error' });
      }
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const unbind = async () => {
    try {
      const r = await apiService().delete(`notifications/registrations/${workerId}`, 'telegram');
      if (r.success) {
        setSnack({ open: true, msg: 'Lidhja u hoq', severity: 'success' });
        setLink(null);
        loadRegs();
      }
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: 'error' });
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setSnack({ open: true, msg: 'U kopjua', severity: 'success' });
  };

  const TELEGRAM_BLUE = '#229ED9';
  const statusChip = isLinked
    ? <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="I lidhur" size="small" color="success" sx={{ height: 22, fontWeight: 600 }} />
    : <Chip label="Pa lidhje" size="small" sx={{ height: 22, fontWeight: 600, bgcolor: 'grey.100', color: 'text.secondary', border: 0 }} />;

  return (
    <>
      <IntegrationCard
        icon={<TelegramIcon />}
        brandColor={TELEGRAM_BLUE}
        title="Telegram"
        description="Njoftime automatike përmes bot-it të Telegram-it pasi punëtori të lidhë llogarinë me një link të një-përdorimshëm."
        status={statusChip}
      >
        {isLinked ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary' }}>
                {tg.external_username ? `@${tg.external_username}` : 'Telegram bound'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                chat_id: <Box component="code" sx={{ fontFamily: 'monospace' }}>{tg.external_id}</Box>
                {tg.verified_at && <> · lidhur më {formatDMY(tg.verified_at)}</>}
              </Typography>
            </Box>
            <Button color="error" variant="text" startIcon={<LinkOffIcon />} onClick={unbind}>
              Hiq lidhjen
            </Button>
          </Stack>
        ) : !link ? (
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <TelegramIcon />}
            onClick={generate}
            disabled={generating}
            sx={{ bgcolor: TELEGRAM_BLUE, '&:hover': { bgcolor: '#1B8BC0' }, height: 40 }}
          >
            Gjenero linkun e lidhjes
          </Button>
        ) : (
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
            <Stack spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Linku është gati ({botUsername}) — skadon për 24 orë
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all', p: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {link}
                </Box>
                <Tooltip title="Kopjo">
                  <IconButton size="small" onClick={() => copy(link)}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Hape në Telegram">
                  <IconButton size="small" component="a" href={link} target="_blank" rel="noopener noreferrer">
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                Punëtori duhet ta hapë këtë link nga telefoni i tij, pastaj shtyp <strong>Start</strong> te bot-i.
              </Typography>
              <Button variant="text" onClick={loadRegs} sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                Kontrollo nëse u lidh
              </Button>
            </Stack>
          </Box>
        )}
      </IntegrationCard>


      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

// ── Expiry chip — uses semantic theme colors ────────────────────────────────
function ExpiryChip({ date }) {
  if (!date) return null;
  const exp = new Date(date);
  const now = new Date();
  const daysLeft = Math.ceil((exp - now) / 86400000);
  if (daysLeft < 0)  return <Chip size="small" label="E skaduar" color="error"   sx={{ height: 20, fontWeight: 600 }} />;
  if (daysLeft < 30) return <Chip size="small" label={`${daysLeft} ditë`} color="warning" sx={{ height: 20, fontWeight: 600 }} />;
  return <Chip size="small" icon={<VerifiedOutlinedIcon sx={{ fontSize: 12 }} />} label="E vlefshme" color="success" sx={{ height: 20, fontWeight: 600, '& .MuiChip-icon': { ml: 0.5 } }} />;
}

const normalizeWorker = (w = {}) => ({
  ...w,
  Id:               w.Id              ?? w.id,
  FirstName:        w.FirstName       ?? w.first_name    ?? w.firstname,
  ParentName:       w.ParentName      ?? w.parent_name,
  LastName:         w.LastName        ?? w.last_name     ?? w.lastname,
  PersonalId:       w.PersonalId      ?? w.PersonalNumber ?? w.personal_id  ?? w.personal_number,
  EmployeeCode:     w.EmployeeCode     ?? w.employee_code,
  Birthday:         w.Birthday        ?? w.birthday      ?? w.DateOfBirth   ?? w.date_of_birth,
  Gender:           w.Gender          ?? w.gender,
  MaritalStatus:    w.MaritalStatus   ?? w.marital_status,
  Email:            w.Email           ?? w.email,
  Phone:            w.Phone           ?? w.phone,
  Qualification:    w.Qualification   ?? w.qualification,
  Address:          w.Address         ?? w.address,
  Place:            w.Place           ?? w.place         ?? w.city,
  PostalCode:       w.PostalCode      ?? w.postal_code,
  Country:          w.Country         ?? w.country,
  BankName:         w.BankName        ?? w.bank_name,
  BankAccount:      w.BankAccount     ?? w.bank_account,
  SecondJob:        (w.SecondJob ?? w.second_job) === true,
  Residency:        (w.Residency ?? w.residency) || 'resident',
  SalaryType:       (w.SalaryType ?? w.salary_type) === 'net' ? 'net' : 'gross',
  Department:       w.Department      ?? w.department    ?? w.department_id,
  Position:         w.Position        ?? w.position,
  StartDate:        w.StartDate       ?? w.start_date    ?? w.HireDate      ?? w.hire_date,
  EndDate:          w.EndDate         ?? w.end_date,
  WorkType:         w.WorkType        ?? w.work_type,
  SalaryType:       w.SalaryType      ?? w.salary_type,
  BaseSalary:       w.BaseSalary      ?? w.base_salary,
  HourSalary:       w.HourSalary      ?? w.hour_salary,
  HourSalaryWeekend:w.HourSalaryWeekend ?? w.hour_salary_weekend,
  HourSalaryHoliday:w.HourSalaryHoliday ?? w.hour_salary_holiday,
  BrutoNeto:        w.BrutoNeto       ?? w.bruto_neto,
  EmployeeTax:      w.EmployeeTax     ?? w.employee_tax,
  EmployerTax:      w.EmployerTax     ?? w.employer_tax,
  Image:            w.Image           ?? w.image,
  Active:           w.Active          ?? w.active,
  ATKRegistered:    w.ATKRegistered   ?? w.atk_registered ?? w.atkRegistered,
  ATKRegisteredAt:  w.ATKRegisteredAt ?? w.atk_registered_at ?? w.atkRegisteredAt,
  PensionNumber:    w.PensionNumber   ?? w.pension_number,
  WorkPermitNumber: w.WorkPermitNumber  ?? w.work_permit_number  ?? '',
  WorkPermitIssued: w.WorkPermitIssued  ?? w.work_permit_issued  ?? '',
  WorkPermitExpires:w.WorkPermitExpires ?? w.work_permit_expires ?? '',
  HealthInsPersonal:+(w.HealthInsPersonal ?? w.health_ins_personal ?? 0),
  HealthInsFamily:  +(w.HealthInsFamily   ?? w.health_ins_family   ?? 0),
  HealthInsNumber:  w.HealthInsNumber  ?? w.health_ins_number  ?? '',
  HealthInsIssued:  w.HealthInsIssued  ?? w.health_ins_issued  ?? '',
  HealthInsExpires: w.HealthInsExpires ?? w.health_ins_expires ?? '',
  SupervisorId:     w.SupervisorId    ?? w.supervisor_id,
  SupervisorName:   w.SupervisorName  ?? w.supervisor_name,
  EmploymentEndDate:w.EmploymentEndDate ?? w.employment_end_date,
  ActivationDate:   w.ActivationDate  ?? w.activation_date ?? '',
  SeparationType:   w.SeparationType  ?? w.separation_type,
  SeparationReason: w.SeparationReason ?? w.separation_reason,
  Notes:            w.Notes           ?? w.notes,
  ContractExtension:w.ContractExtension ?? w.contract_extension,
  Template:         w.Template        ?? w.template,
  WithTax:          w.WithTax         ?? w.with_tax,
});

const fmtMoney = (v) => {
  const n = +v || 0;
  return n.toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

export default function WorkerDetail() {
  const { id } = useParams();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [departments, setDepartments] = useState([]);
  const [allWorkers, setAllWorkers]   = useState([]);

  useEffect(() => {
    if (!id) { setLoading(false); setError('ID e punëtorit mungon'); return; }
    let cancelled = false;

    (async () => {
      try {
        const [wRes, dRes, allW] = await Promise.all([
          apiService().query('getWorker', 'id=' + id),
          apiService().get('department'),
          apiService().get('workers').catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          if (wRes?.success && wRes.data?.[0]) {
            setWorker(normalizeWorker(wRes.data[0]));
          } else {
            setError('Punëtori nuk u gjet.');
          }
          if (dRes?.success) setDepartments(dRes.data || []);
          setAllWorkers(allW?.data || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Gabim gjatë ngarkimit.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id]);

  const deptObj    = departments.find(d => String(d.Id || d.id) === String(worker?.Department));
  const deptName   = deptObj?.Name || deptObj?.name || null;
  const fullName   = [worker?.FirstName, worker?.LastName].filter(Boolean).join(' ') || '—';
  const initials   = fullName !== '—' ? fullName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase() : '?';
  const isActive   = worker ? (worker.Active !== false && worker.active !== false) : true;
  const genderMap  = { '0': 'Femër', '1': 'Mashkull', f: 'Femër', m: 'Mashkull', female: 'Femër', male: 'Mashkull' };
  const genderLabel= genderMap[String(worker?.Gender || '').toLowerCase()] || worker?.Gender || '—';
  const msMap      = { single: 'Beqar/e', married: 'I/E martuar', divorced: 'I/E divorcuar', widowed: 'I/E ve', '0': 'Beqar/e', '1': 'I/E martuar' };
  const msLabel    = msMap[String(worker?.MaritalStatus || '').toLowerCase()] || worker?.MaritalStatus || '—';
  const supObj     = allWorkers.find(w => String(w.Id || w.id) === String(worker?.SupervisorId));
  const supName    = worker?.SupervisorName || (supObj ? `${supObj.FirstName || supObj.first_name || ''} ${supObj.LastName || supObj.last_name || ''}`.trim() : '');
  // Workers this person directly manages (their supervisor_id points here).
  const myId       = worker?.Id ?? worker?.id;
  const subordinates = (allWorkers || []).filter(w =>
    myId != null &&
    String(w.SupervisorId ?? w.supervisor_id ?? '') === String(myId) &&
    String(w.Id ?? w.id) !== String(myId)
  );
  // Lloji i punës ruhet si id numerike (1/2 — të njëjtat si te lista e
  // `RegisterWorker`/`AddWorkerContract`), ndaj harta duhet t'i njohë të dyja
  // format; përndryshe ekrani shkruan «1» dhe lexuesi mbetet me kodin në dorë.
  const wtMap      = {
    fulltime: 'Kohë e plotë', parttime: 'Kohë e pjesshme',
    contract: 'Kontratë', intern: 'Praktikant',
    '1': 'Kohë e plotë', '2': 'Kohë e pjesshme', '3': 'Kontratë', '4': 'Praktikant',
  };
  const wtLabel    = wtMap[String(worker?.WorkType ?? '').toLowerCase()] || worker?.WorkType;
  const stMap      = { gross: 'Bruto', net: 'Neto', bruto: 'Bruto', neto: 'Neto' };
  const stLabel    = stMap[String(worker?.SalaryType ?? '').toLowerCase()] || worker?.SalaryType;

  if (loading) {
    return <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}><LinearProgress sx={{ borderRadius: 999 }} /></Box>;
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/workerlist" startIcon={<ArrowBackIcon />} sx={{ mt: 2, textTransform: 'none' }}>
          Kthehu te lista
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 0, sm: 0 }, py: { xs: 1, sm: 1.5 } }}>
      <Stack spacing={{ xs: 2, md: 3 }}>

        {/* ── Header toolbar ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Tooltip title="Kthehu">
              <IconButton component={Link} to="/workerlist" sx={{ width: 36, height: 36, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  letterSpacing: '-0.025em',
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                {fullName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
                Profil i punëtorit{worker?.Position ? ` · ${worker.Position}` : ''}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
            component={Link}
            to={`/workerlist/editworker/${id}`}
            sx={{ height: 40, borderRadius: 1.5, px: 2 }}
          >
            Ndrysho
          </Button>
        </Stack>

        {/* ── Hero card (clean, no top gradient bar) ── */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1, borderColor: 'divider',
            p: { xs: 2.5, sm: 3 },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2.5, sm: 3 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            {/* Avatar — soft teal background, no heavy ring */}
            <Box
              sx={{
                position: 'relative',
                flexShrink: 0,
                alignSelf: { xs: 'center', sm: 'flex-start' },
              }}
            >
              <Avatar
                src={worker?.Image || undefined}
                sx={{
                  width: 80, height: 80,
                  bgcolor: 'primary.soft',
                  color: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  border: 0,
                }}
              >
                {!worker?.Image && initials}
              </Avatar>
              {/* Online indicator: small dot, soft ring */}
              <Box
                sx={{
                  position: 'absolute', bottom: 2, right: 2,
                  width: 14, height: 14, borderRadius: '50%',
                  bgcolor: isActive ? 'success.main' : 'grey.400',
                  border: '2.5px solid',
                  borderColor: 'background.paper',
                }}
              />
            </Box>

            {/* Identity + metadata */}
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', sm: '1.375rem' },
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    color: 'text.primary',
                  }}
                >
                  {fullName}
                </Typography>
                <Chip
                  label={isActive ? 'Aktiv' : 'Joaktiv'}
                  size="small"
                  color={isActive ? 'success' : 'warning'}
                  sx={{ height: 22, fontWeight: 600 }}
                />
                {/* Regjistruar sot, fillon më vonë: ekziston në HR, por llogaria
                    hyn vetëm nga data e aktivizimit (workers.activation_date). */}
                {isActive && isPendingActivation(worker?.ActivationDate) && (
                  <Chip
                    icon={<HourglassTopRoundedIcon sx={{ fontSize: 14 }} />}
                    label={`Në pritje të aktivizimit · nga ${formatDMY(worker.ActivationDate)}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22, fontWeight: 600 }}
                  />
                )}
              </Stack>

              {/* Role line — position · department · joined */}
              <Stack
                direction="row"
                spacing={1.5}
                flexWrap="wrap"
                alignItems="center"
                sx={{ mt: 1, rowGap: 0.75 }}
              >
                {worker?.Position && (
                  <Stack direction="row" alignItems="center" spacing={0.625}>
                    <WorkOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                      {worker.Position}
                    </Typography>
                  </Stack>
                )}
                {deptName && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300' }} />
                    <Chip
                      label={deptName}
                      size="small"
                      sx={{
                        height: 22, fontWeight: 600,
                        bgcolor: 'primary.soft',
                        color: 'primary.dark',
                        border: 0,
                      }}
                    />
                  </>
                )}
                {worker?.StartDate && (
                  <>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'grey.300' }} />
                    <Stack direction="row" alignItems="center" spacing={0.625}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                        {formatDMY(worker.StartDate)}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>

              {/* Contact chips */}
              {(worker?.Email || worker?.Phone) && (
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {worker?.Email && (
                    <Chip
                      icon={<EmailIcon sx={{ fontSize: '15px !important' }} />}
                      label={worker.Email}
                      size="small"
                      component="a"
                      href={`mailto:${worker.Email}`}
                      clickable
                      sx={{
                        height: 28,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        bgcolor: 'grey.50',
                        color: 'text.primary',
                        border: 1, borderColor: 'divider',
                        '&:hover': { bgcolor: 'background.paper', borderColor: 'grey.300' },
                        '& .MuiChip-icon': { ml: 1, color: 'text.disabled' },
                      }}
                    />
                  )}
                  {worker?.Phone && (
                    <Chip
                      icon={<CallIcon sx={{ fontSize: '15px !important' }} />}
                      label={worker.Phone}
                      size="small"
                      component="a"
                      href={`tel:${worker.Phone}`}
                      clickable
                      sx={{
                        height: 28,
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        bgcolor: 'grey.50',
                        color: 'text.primary',
                        border: 1, borderColor: 'divider',
                        '&:hover': { bgcolor: 'background.paper', borderColor: 'grey.300' },
                        '& .MuiChip-icon': { ml: 1, color: 'text.disabled' },
                      }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        </Card>

        {/* ── 2-col responsive grid ── */}
        {/* `alignItems:flex-start` — pa të, dy kartelat e një rreshti zgjaten sa
            më e larta dhe ajo e shkurtra mbetet me një pëllëmbë hapësirë boshe. */}
        {/* Kartelat paloset si mur tullash, jo si tabelë: me rreshta të fiksuar,
            një kartelë njërreshtëshe pranë një kartele dhjetërreshtëshe linte
            gjysmën e rreshtit bosh. Me kolona CSS secila rrjedh pas asaj sipër
            dhe vrimat mbyllen vetë. */}
        <Box sx={{ columnCount: { xs: 1, md: 2 }, columnGap: 2.5 }}>
          {/* Personal */}
          <Box sx={CARD_COL}>
            <DetailSection title="Të dhëna personale" icon={<PersonIcon />}>
              <DetailField label="Kodi i punëtorit (ID)" value={worker?.EmployeeCode} />
              <DetailField label="Emri" value={worker?.FirstName} />
              <DetailField label="Mbiemri" value={worker?.LastName} />
              <DetailField label="Emri i babait" value={worker?.ParentName} />
              <DetailField label="Numri personal (ID)" value={worker?.PersonalId} />
              {/* Mosha rri pranë datës, jo si fushë më vete: është e njëjta e
                  dhënë e lexuar ndryshe, dhe kur data mungon nuk ka çka të thotë. */}
              <DetailField
                label="Datëlindja"
                value={formatDMY(worker?.Birthday) || worker?.Birthday}
                suffix={(() => {
                  const a = ageFromBirthDate(worker?.Birthday);
                  if (a === null) return undefined;
                  // Mosha si pilulë, jo si tekst gri: është e vetmja shifër këtu
                  // që ndryshon vetë me kalimin e kohës, dhe syri duhet ta gjejë
                  // pa e lexuar datën shkronjë për shkronjë.
                  return (
                    <Box component="span" sx={{
                      display: 'inline-block', ml: 0.25, px: 0.875, py: 0.125,
                      borderRadius: 1, bgcolor: 'primary.soft', color: 'primary.onSoft',
                      fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.01em',
                      verticalAlign: 'middle',
                    }}>
                      {a} vjeç
                    </Box>
                  );
                })()}
              />
              <DetailField label="Gjinia" value={genderLabel} />
              <DetailField label="Statusi martesor" value={msLabel} />
              <DetailField label="Kualifikimi" value={worker?.Qualification} />
            </DetailSection>
          </Box>

          {/* Contact */}
          <Box sx={CARD_COL}>
            <DetailSection title="Kontakti & Vendbanimi" icon={<EmailIcon />}>
              <DetailField label={`Email personal${worker?.PreferredEmail !== 'work' ? ' · për njoftime' : ''}`} value={worker?.Email} span={2} />
              <DetailField label={`Email i punës${worker?.PreferredEmail === 'work' ? ' · për njoftime' : ''}`} value={worker?.WorkEmail} span={2} />
              <DetailField label="Telefoni" value={worker?.Phone} />
              <DetailField label="Vendbanimi" value={worker?.Place} />
              <DetailField label="Adresa" value={worker?.Address} span={2} />
              <DetailField label="Kodi postar" value={worker?.PostalCode} />
              <DetailField label="Shteti" value={worker?.Country} />
            </DetailSection>
          </Box>

          {/* Employment */}
          <Box sx={CARD_COL}>
            <DetailSection title="Punësimi" icon={<WorkOutlineIcon />}>
              <DetailField label="Pozita" value={worker?.Position} />
              <DetailField label="Departamenti" value={deptName} />
              <DetailField label="Lloji i punës" value={wtLabel} />
              <DetailField label="Mbikëqyrësi" value={supName} />
              <DetailField label="Data e fillimit" value={formatDMY(worker?.StartDate) || worker?.StartDate} />
              <DetailField label="Data e mbarimit" value={worker?.EndDate ? formatDMY(worker.EndDate) : 'Pa afat'} />
            </DetailSection>
          </Box>

          {/* Under management — shown only for supervisors who have direct reports.
              Gjysmë rreshti, jo i tërë: si kartelë e plotë e këpuste rreshtin dhe
              linte gjysmën e mëparshme (Punësimi) me hapësirë boshe pranë. */}
          {subordinates.length > 0 && (
            <Box sx={CARD_COL}>
              <DetailSection title={`Nën menaxhim (${subordinates.length})`} icon={<SupervisorAccountOutlinedIcon />}>
                {subordinates.map((s) => {
                  const sid = s.Id ?? s.id;
                  const sname = `${s.FirstName || s.first_name || ''} ${s.LastName || s.last_name || ''}`.trim() || '—';
                  const sinit = sname !== '—' ? sname.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase() : '?';
                  const sdeptObj = departments.find(d => String(d.Id || d.id) === String(s.Department ?? s.department_id));
                  const sdept = sdeptObj?.Name || sdeptObj?.name || s.DepartmentName || '';
                  const spos = s.Position || s.position || '';
                  const sactive = s.Active !== false && s.active !== false;
                  return (
                    <Grid item xs={12} sm={6} key={sid}>
                      <Box
                        component={Link}
                        to={`/workerlist/worker/${sid}`}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25,
                          borderRadius: 2, border: 1, borderColor: 'divider',
                          textDecoration: 'none', color: 'inherit', transition: 'all .15s',
                          '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.soft' },
                        }}
                      >
                        <Avatar sx={{
                          width: 36, height: 36, fontSize: 13, fontWeight: 700,
                          bgcolor: sactive ? 'primary.soft' : 'grey.200',
                          color: sactive ? 'primary.main' : 'text.disabled',
                        }}>{sinit}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '.85rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {sname}{!sactive && <Chip label="Joaktiv" size="small" sx={{ ml: 0.75, height: 16, fontSize: '.6rem' }} />}
                          </Typography>
                          <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {[spos, sdept].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </DetailSection>
            </Box>
          )}

          {/* Salary */}
          <Box sx={CARD_COL}>
            <DetailSection title="Paga" icon={<PaymentsOutlinedIcon />}>
              <DetailField label="Lloji i pagës" value={stLabel} />
              <DetailField label="Paga bazë" value={worker?.BaseSalary ? fmtMoney(worker.BaseSalary) : null} />
              <DetailField label="Paga/orë" value={worker?.HourSalary ? fmtMoney(worker.HourSalary) : null} />
              <DetailField label="Bruto/Neto" value={worker?.BrutoNeto} />
              <DetailField label="Paga/orë (fundjavë)" value={worker?.HourSalaryWeekend ? fmtMoney(worker.HourSalaryWeekend) : null} />
              <DetailField label="Paga/orë (festa)" value={worker?.HourSalaryHoliday ? fmtMoney(worker.HourSalaryHoliday) : null} />
              <DetailField label="Tatim punëmarrësi" value={worker?.EmployeeTax ? worker.EmployeeTax + '%' : null} />
              <DetailField label="Tatim punëdhënësi" value={worker?.EmployerTax ? worker.EmployerTax + '%' : null} />
            </DetailSection>
          </Box>

          {/* Health Insurance */}
          <Box sx={CARD_COL}>
            <DetailSection
              title="Sigurimi shëndetësor"
              icon={<MedicalServicesOutlinedIcon />}
              headerExtra={
                <>
                  <Chip
                    size="small"
                    label="Zbritet nga rroga neto"
                    sx={{ height: 20, fontWeight: 500, bgcolor: 'grey.100', color: 'text.secondary', border: 0 }}
                  />
                  <ExpiryChip date={worker?.HealthInsExpires} />
                </>
              }
            >
              <DetailField label="Shuma personale" value={worker?.HealthInsPersonal ? fmtMoney(worker.HealthInsPersonal) : null} />
              <DetailField label="Shuma familjare" value={worker?.HealthInsFamily ? fmtMoney(worker.HealthInsFamily) : null} />
              <DetailField label="Numri i sigurimit" value={worker?.HealthInsNumber} />
              <DetailField label="Data e lëshimit" value={worker?.HealthInsIssued ? formatDMY(worker.HealthInsIssued) : null} />
              <DetailField label="Data e skadencës" value={worker?.HealthInsExpires ? formatDMY(worker.HealthInsExpires) : null} />
            </DetailSection>
          </Box>

          {/* Work Permit */}
          <Box sx={CARD_COL}>
            <DetailSection
              title="Leja e punës"
              icon={<DescriptionOutlinedIcon />}
              headerExtra={<ExpiryChip date={worker?.WorkPermitExpires} />}
            >
              <DetailField label="Numri i lejes" value={worker?.WorkPermitNumber} />
              <DetailField label="Data e lëshimit" value={worker?.WorkPermitIssued ? formatDMY(worker.WorkPermitIssued) : null} />
              <DetailField label="Data e skadencës" value={worker?.WorkPermitExpires ? formatDMY(worker.WorkPermitExpires) : null} />
            </DetailSection>
          </Box>

          {/* ATK & Pension */}
          <Box sx={CARD_COL}>
            <DetailSection title="ATK & Pensioni" icon={<ApartmentIcon />}>
              <DetailField label="ATK Regjistruar" value={worker?.ATKRegistered ? 'Po' : 'Jo'} />
              <DetailField label="Data e regjistrimit" value={worker?.ATKRegisteredAt ? formatDMY(worker.ATKRegisteredAt) : null} />
              <DetailField label="Numri i pensionit" value={worker?.PensionNumber} />
              <DetailField label="Me tatim" value={worker?.WithTax === true || worker?.WithTax === 'true' ? 'Po' : worker?.WithTax === false ? 'Jo' : null} />
              {/* Ndryshon tatimin, ndaj rri me ATK-në e jo me të dhënat bazë:
                  punësimi sekondar tatohet pa brezin e liruar. Shfaqet gjithnjë
                  — bosh do të lexohej si "e paplotësuar", jo si "primar". */}
              <Grid item xs={6} sm={4} md={3}>
                <Typography sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 500, color: 'text.disabled', letterSpacing: '0.04em', lineHeight: 1.3 }}>
                  Lloji i punësimit
                </Typography>
                <Chip
                  size="small"
                  label={worker?.SecondJob ? 'Punë Sekondare' : 'Punë Primare'}
                  sx={{
                    height: 22, fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: worker?.SecondJob ? '#FFEDD5' : '#DCFCE7',
                    color:   worker?.SecondJob ? '#C2410C' : '#15803D',
                    border: 0,
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Typography sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 500, color: 'text.disabled', letterSpacing: '0.04em', lineHeight: 1.3 }}>
                  Rezidenca
                </Typography>
                <Chip
                  size="small"
                  label={worker?.Residency === 'foreign' ? 'Punëtor i huaj' : 'Vendas'}
                  sx={{
                    height: 22, fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: worker?.Residency === 'foreign' ? '#DBEAFE' : '#F1F5F9',
                    color:   worker?.Residency === 'foreign' ? '#1D4ED8' : '#475569',
                    border: 0,
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Typography sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem', fontWeight: 500, color: 'text.disabled', letterSpacing: '0.04em', lineHeight: 1.3 }}>
                  Paga dakorduar
                </Typography>
                <Chip
                  size="small"
                  label={worker?.SalaryType === 'net' ? 'Neto' : 'Bruto'}
                  sx={{
                    height: 22, fontWeight: 700, fontSize: '0.7rem',
                    // Neto/Bruto është LLOJ, jo status — asnjëra s'është më e mirë
                    // se tjetra. Vjollca ndaj "Neto" i jepte një peshë që s'e ka;
                    // të dyja rrinë neutrale dhe dallohen nga vetë fjala.
                    bgcolor: '#F1F5F9',
                    color:   '#475569',
                    border: 0,
                  }}
                />
              </Grid>
            </DetailSection>
          </Box>

          {/* Banka */}
          <Box sx={CARD_COL}>
            <DetailSection title="Banka" icon={<AccountBalanceIcon />}>
              <DetailField label="Banka" value={worker?.BankName} />
              <DetailField label="Nr. i llogarisë" value={worker?.BankAccount} span={2} />
            </DetailSection>
          </Box>

          {/* Notes — full width if present */}
          {(worker?.Notes || worker?.SeparationReason || worker?.SeparationType) && (
            <Box sx={CARD_COL}>
              <DetailSection title="Shënime & Ndërprerja" icon={<NotesIcon />}>
                {worker?.SeparationType && <DetailField label="Lloji i ndërprerjes" value={worker.SeparationType} />}
                {worker?.EmploymentEndDate && <DetailField label="Data e ndërprerjes" value={formatDMY(worker.EmploymentEndDate)} />}
                {worker?.ActivationDate && <DetailField label="Data e aktivizimit" value={formatDMY(worker.ActivationDate)} />}
                {worker?.SeparationReason && <DetailField label="Arsyeja e ndërprerjes" value={worker.SeparationReason} span={2} />}
                {worker?.Notes && <DetailField label="Shënime" value={worker.Notes} span={2} />}
              </DetailSection>
            </Box>
          )}

          {/* Integrations: Slack DM + Telegram link */}
          <SlackBinding workerId={id} />
          <TelegramBinding workerId={id} />
        </Box>

        {/* Custom fields */}
        <ModuleCustomFields moduleKey="workers" recordId={id} mode="detail" />

      </Stack>
    </Box>
  );
}
